/**
 * Browser PostHog — identified-only (DeepJournal pattern).
 *
 * Hard rules:
 * - Never `init` without a known eligible user (email, or IOP `iop-demo-*`).
 * - Init → identify → $pageview in one step; never leave an anonymous session.
 * - Impersonation / logged-out / missing user → reset + capture no-ops (zero traffic).
 * - Distinct-id change (email ↔ iop-demo-*) → reset() then identify (no alias, no second init).
 * - All captures gated on `identified`.
 * - Session recording is sampled + optionally deferred on runway paths.
 * - Per-account / per-user `sessionRecordingForced` (from data-posthog-user)
 *   overrides to 100% sample + runway recording.
 * - `sessionRecordingBlocked` (super admins) never records, even when forced.
 *
 * Bootstrap: app/views/shared/_posthog_browser_config.html.erb
 * User: <body data-posthog-user="..."> (omitted when impersonating).
 */
import posthog from "posthog-js"

const isBrowser = typeof window !== "undefined"
const APP = "aifactory"

/** Default fraction of identified sessions that may record (0–1). */
export const DEFAULT_SESSION_RECORDING_SAMPLE_RATE = 0.1

/** Path prefixes where recording is deferred when sessionRecordingOnRunway is false. */
export const RUNWAY_PATH_PREFIXES = ["/legacy/runway", "/legacy/dev/graph_runway"]

let booted = false
/** True only after init + identify for an eligible user. Gates all captures. */
let identified = false
let analyticsEnabled = false

/** Sampling decision for the current SDK boot (rolled once per boot / tenant). */
let recordingSampleDecided = false
let recordingSampled = false
/** True while RunwayShell (or path sync) has asked us to pause recording. */
let recordingDeferredForRunway = false
/** Sticky tenancy keys so turbo account switches re-roll recording policy. */
let lastRecordingAccountId = null
let lastSessionRecordingForced = null
let lastSessionRecordingBlocked = null
/** Effective recording policy from the last sync (forced overrides bootstrap). */
let effectiveRecordingCfg = null
/** Last identified distinct_id — reset before identify when it changes. */
let lastDistinctId = null

function readBootstrapConfig() {
  if (!isBrowser) return null
  const cfg = window.__AIFACTORY_POSTHOG__
  if (!cfg?.apiKey) return null
  return cfg
}

function readUserFromDOM() {
  if (!isBrowser) return null
  const body = document?.body
  if (!body) return null
  if ((body.dataset.posthogEnabled || "").toString() !== "true") return null
  // Impersonation: never send analytics (matches server posthog_user_payload).
  if ((body.dataset.impersonating || "").toString() === "true") return null

  const rawUser = body.dataset.posthogUser
  if (!rawUser) return null

  try {
    return JSON.parse(rawUser)
  } catch (e) {
    console.warn("[posthog] Failed to parse user data", e)
    return null
  }
}

/**
 * Merge bootstrap env rates with per-user/account forced recording from the user payload.
 * Blocked (super admin) always wins → sample rate 0.
 * @param {object|null} bootstrap
 * @param {object|null} user
 */
export function effectiveSessionRecordingConfig(bootstrap, user) {
  if (!bootstrap) return null
  if (user?.sessionRecordingBlocked === true) {
    return {
      ...bootstrap,
      sessionRecordingSampleRate: 0,
      sessionRecordingOnRunway: false,
    }
  }
  const forced = user?.sessionRecordingForced === true
  if (forced) {
    return {
      ...bootstrap,
      sessionRecordingSampleRate: 1,
      sessionRecordingOnRunway: true,
    }
  }
  return bootstrap
}

function accountIdFromUser(user) {
  const id = user?.properties?.account_id
  if (id == null || id === "") return null
  return String(id)
}

function appFromUser(user) {
  const app = user?.properties?.app
  return typeof app === "string" && app.length > 0 ? app : APP
}

function registerSuperProperties(bootstrap, app, client = posthog) {
  const regs = { app }
  if (bootstrap?.environment) regs.environment = bootstrap.environment
  try {
    client.register?.(regs)
  } catch (_) {
    /* ignore */
  }
}

/**
 * Parse a sample rate from bootstrap/env. Clamps to [0, 1]; invalid → default.
 * @param {unknown} value
 * @param {number} [fallback]
 */
export function parseSessionRecordingSampleRate(
  value,
  fallback = DEFAULT_SESSION_RECORDING_SAMPLE_RATE,
) {
  if (value === null || value === undefined || value === "") return fallback
  const n = typeof value === "number" ? value : Number.parseFloat(String(value))
  if (!Number.isFinite(n)) return fallback
  if (n <= 0) return 0
  if (n >= 1) return 1
  return n
}

/**
 * @param {string} [pathname]
 */
export function isRunwayPath(pathname) {
  const path =
    pathname ??
    (isBrowser ? window.location?.pathname || "" : "")
  if (!path) return false
  return RUNWAY_PATH_PREFIXES.some(
    (prefix) => path === prefix || path.startsWith(`${prefix}/`),
  )
}

/**
 * Whether recording is allowed on the current (or given) path given bootstrap.
 * When `sessionRecordingOnRunway` is true, runway paths are allowed.
 * @param {object|null} cfg
 * @param {string} [pathname]
 */
export function isSessionRecordingAllowedOnPath(cfg, pathname) {
  if (cfg?.sessionRecordingOnRunway === true) return true
  return !isRunwayPath(pathname)
}

/**
 * @param {number} sampleRate
 * @param {() => number} [rng]
 */
export function shouldSampleSessionRecording(sampleRate, rng = Math.random) {
  const rate = parseSessionRecordingSampleRate(sampleRate, 0)
  if (rate <= 0) return false
  if (rate >= 1) return true
  try {
    return rng() < rate
  } catch (_) {
    return false
  }
}

function ensureRecordingSampleDecision(cfg) {
  if (recordingSampleDecided) return
  recordingSampleDecided = true
  const rate = parseSessionRecordingSampleRate(cfg?.sessionRecordingSampleRate)
  recordingSampled = shouldSampleSessionRecording(rate)
}

function stopSessionRecordingSafe() {
  try {
    if (typeof posthog.stopSessionRecording === "function") {
      posthog.stopSessionRecording()
    }
  } catch (_) {
    /* ignore missing / broken SDK methods */
  }
}

function startSessionRecordingSafe(client = posthog) {
  try {
    if (typeof client?.startSessionRecording === "function") {
      client.startSessionRecording()
    }
  } catch (_) {
    /* ignore missing / broken SDK methods */
  }
}

/**
 * Start recording only when sampled, not deferred, and path allows it.
 * Feature-detects PostHog APIs — never throws.
 */
function maybeStartSessionRecording(cfg, client = posthog) {
  if (!booted || !identified || !recordingSampled) return
  if (recordingDeferredForRunway) return
  if (!isSessionRecordingAllowedOnPath(cfg)) return
  startSessionRecordingSafe(client)
}

function syncSessionRecordingWithPath(cfg) {
  if (!booted || !identified || !recordingSampled) return
  if (!isSessionRecordingAllowedOnPath(cfg)) {
    stopSessionRecordingSafe()
    return
  }
  maybeStartSessionRecording(cfg)
}

/**
 * When account_id / forced / blocked changes (turbo tenant switch),
 * re-roll sampling and stop recording if no longer eligible.
 * If forced stays true across an account_id change, keep recording (user-level force).
 * Blocked sessions never sample.
 */
function syncRecordingTenancy(user, cfg) {
  const accountId = accountIdFromUser(user)
  const forced = user?.sessionRecordingForced === true
  const blocked = user?.sessionRecordingBlocked === true
  const forcedUnchanged = lastSessionRecordingForced === forced
  const blockedUnchanged = lastSessionRecordingBlocked === blocked
  const accountChanged = lastRecordingAccountId !== accountId

  // User (or OR) still forced and not blocked: only update sticky account_id.
  if (forced && !blocked && forcedUnchanged && blockedUnchanged && recordingSampleDecided) {
    lastRecordingAccountId = accountId
    effectiveRecordingCfg = cfg
    return
  }

  const changed = accountChanged || !forcedUnchanged || !blockedUnchanged

  if (!changed && recordingSampleDecided) {
    effectiveRecordingCfg = cfg
    return
  }

  lastRecordingAccountId = accountId
  lastSessionRecordingForced = forced
  lastSessionRecordingBlocked = blocked
  effectiveRecordingCfg = cfg
  recordingSampleDecided = false
  recordingSampled = false
  stopSessionRecordingSafe()
  ensureRecordingSampleDecision(cfg)
  if (!recordingSampled) {
    stopSessionRecordingSafe()
  }
}

/**
 * Pause session recording while heavy runway UI is mounted.
 * No-op when effective config allows recording on runway (env or forced).
 */
export function deferSessionRecordingForRunway() {
  const cfg = effectiveRecordingCfg || readBootstrapConfig()
  if (cfg?.sessionRecordingOnRunway === true) return
  recordingDeferredForRunway = true
  stopSessionRecordingSafe()
}

/**
 * Resume recording after leaving runway (only if this session was sampled).
 */
export function resumeSessionRecordingAfterRunway() {
  recordingDeferredForRunway = false
  const cfg = effectiveRecordingCfg || readBootstrapConfig()
  if (!cfg) return
  maybeStartSessionRecording(cfg)
}

/**
 * Clear identity and stop sending. If the SDK was booted, reset so we do not
 * leave an anonymous distinct_id emitting pageleave / captures.
 */
function clearSession() {
  identified = false
  analyticsEnabled = false
  lastDistinctId = null
  recordingSampleDecided = false
  recordingSampled = false
  recordingDeferredForRunway = false
  lastRecordingAccountId = null
  lastSessionRecordingForced = null
  lastSessionRecordingBlocked = null
  effectiveRecordingCfg = null
  if (booted) {
    try {
      posthog.reset()
    } catch (_) {
      /* ignore */
    }
  }
}

function resetIdentityKeepBoot() {
  identified = false
  analyticsEnabled = false
  lastDistinctId = null
  recordingSampleDecided = false
  recordingSampled = false
  lastRecordingAccountId = null
  lastSessionRecordingForced = null
  lastSessionRecordingBlocked = null
  effectiveRecordingCfg = null
  try {
    posthog.reset()
  } catch (_) {
    /* ignore */
  }
}

function bootAndIdentify(bootstrap, user) {
  if (!bootstrap?.apiKey || !user?.distinctId) return

  const app = appFromUser(user)

  if (booted && lastDistinctId && lastDistinctId !== user.distinctId) {
    resetIdentityKeepBoot()
  }

  const cfg = effectiveSessionRecordingConfig(bootstrap, user)
  syncRecordingTenancy(user, cfg)

  if (!booted) {
    posthog.init(bootstrap.apiKey, {
      api_host: bootstrap.host || "https://us.i.posthog.com",
      person_profiles: "identified_only",
      capture_pageview: false,
      capture_pageleave: true,
      // We control start via sample rate + runway defer (feature-detect start/stop).
      disable_session_recording: true,
      session_recording: {
        maskAllInputs: true,
      },
      loaded: (client) => {
        registerSuperProperties(bootstrap, app, client)
        ensureRecordingSampleDecision(cfg)
        maybeStartSessionRecording(cfg, client)
      },
    })
    booted = true
  } else {
    registerSuperProperties(bootstrap, app)
    ensureRecordingSampleDecision(cfg)
  }

  lastDistinctId = user.distinctId
  analyticsEnabled = true
  posthog.identify(user.distinctId, {
    app,
    ...(user.properties || {}),
  })
  identified = true
  posthog.capture("$pageview", { app })
  syncSessionRecordingWithPath(cfg)
}

/**
 * Identified-only sync. Call on DOMContentLoaded / turbo:load.
 * No user / impersonating / disabled → no PostHog traffic.
 */
export function syncPostHog() {
  const cfg = readBootstrapConfig()
  if (!cfg) {
    clearSession()
    return
  }

  const user = readUserFromDOM()
  if (!user?.distinctId) {
    clearSession()
    return
  }

  bootAndIdentify(cfg, user)
}

export function capturePostHog(event, properties) {
  if (!analyticsEnabled || !identified) return
  posthog.capture(event, { app: APP, ...(properties || {}) })
}

export function resetPostHog() {
  clearSession()
}

export const PosthogClient = {
  syncUser: syncPostHog,
  capture: capturePostHog,
  reset: resetPostHog,
  deferSessionRecordingForRunway,
  resumeSessionRecordingAfterRunway,
}

if (isBrowser) {
  document.addEventListener("DOMContentLoaded", syncPostHog)
  document.addEventListener("turbo:load", syncPostHog)
}
